/**
 * ScenarioBuilder — Sprint 55: "Was waere wenn?" Scenario Planning
 *
 * Full-screen slide-in panel for farm-level crop rotation scenarios.
 * Users can swap crops on fields, override yield/price, and see live
 * delta calculations comparing current plan vs. scenario.
 *
 * Accessed from the DB Calculator / Finance detail view.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';

// ── Constants ────────────────────────────────────────────────────

const CROP_EMOJIS = {
  wheat: '\u{1F33E}', rapeseed: '\u{1F33B}', barley: '\u{1F33E}', corn: '\u{1F33D}',
  sugarbeet: '\u{1FAD8}', potato: '\u{1F954}', rye: '\u{1F33E}', grassland: '\u{1F33F}',
  oats: '\u{1F33E}', triticale: '\u{1F33E}', sunflower: '\u{1F33B}', soybean: '\u{1FAD8}',
  peas: '\u{1FADB}',
};

const EUR = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const NUM_DE = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

function cropEmoji(code) {
  return CROP_EMOJIS[code] || '\u{1F331}';
}

// ── Sub-components ───────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent animate-spin" />
    </div>
  );
}

function DeltaBadge({ current, scenario }) {
  if (current == null || scenario == null) return null;
  const delta = scenario - current;
  const pct = current !== 0 ? ((delta / Math.abs(current)) * 100) : 0;
  const positive = delta >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        positive
          ? 'bg-green-500/15 text-green-400'
          : 'bg-red-500/15 text-red-400'
      }`}
    >
      {positive ? '+' : ''}{EUR.format(delta)}
      <span className="text-[10px] font-medium opacity-80">
        ({positive ? '+' : ''}{pct.toFixed(1)}%)
      </span>
    </span>
  );
}

function InlineNumberInput({ value, onSave, unit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const handleOpen = () => {
    setDraft(String(value ?? ''));
    setEditing(true);
  };

  const handleSave = () => {
    const val = parseFloat(draft.replace(',', '.'));
    if (!isNaN(val)) onSave(val);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setEditing(false);
          }}
          onBlur={handleSave}
          autoFocus
          className="w-20 text-xs text-right bg-[var(--aif-bg-input)] border border-[var(--aif-color-primary)] rounded px-1 py-0.5 text-[var(--aif-text-primary)] outline-none"
          step="any"
        />
        {unit && <span className="text-[9px] text-[var(--aif-text-muted)]">{unit}</span>}
      </div>
    );
  }

  return (
    <span
      className="text-xs font-medium text-[var(--aif-text-primary)] cursor-pointer hover:text-[var(--aif-color-primary)] transition-colors underline decoration-dotted underline-offset-2"
      onClick={handleOpen}
    >
      {value != null ? NUM_DE.format(value) : '\u2014'}
      {unit && <span className="text-[9px] text-[var(--aif-text-muted)] ml-0.5 no-underline">{unit}</span>}
    </span>
  );
}

function SaveDialog({ onSave, onCancel, t }) {
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-[var(--aif-z-modal)] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm bg-[var(--aif-bg-page)] rounded-2xl border border-[var(--aif-border-default)] p-5 space-y-4 shadow-xl">
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {t('scenario.saveTitle')}
        </h3>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onSave(name.trim()); }}
          placeholder={t('scenario.namePlaceholder')}
          autoFocus
          className="w-full px-3 py-2 text-sm bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl text-[var(--aif-text-primary)] outline-none focus:border-[var(--aif-color-primary)] transition-colors"
        />
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-xs font-medium rounded-xl border border-[var(--aif-border-default)] text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-surface)] transition-colors"
          >
            {t('form.cancel')}
          </button>
          <button
            onClick={() => name.trim() && onSave(name.trim())}
            disabled={!name.trim()}
            className="flex-1 py-2 text-xs font-medium rounded-xl bg-[var(--aif-color-primary)] text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {t('form.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SavedScenariosPanel({ token, onLoad, onClose, t }) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/v1/aif/finance/scenarios', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setScenarios(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDelete = async (id) => {
    try {
      // TODO(5b): activate concurrency — scenario is 3WM
      await apiClient.delete(`/api/v1/aif/finance/scenarios/${id}`, { entityType: 'scenario', queueOnOffline: true });
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[var(--aif-z-modal)] flex items-end sm:items-center justify-center bg-black/50">
      <div className="w-full max-w-md max-h-[80vh] bg-[var(--aif-bg-page)] rounded-t-2xl sm:rounded-2xl border border-[var(--aif-border-default)] flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
          <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
            {t('scenario.savedScenarios')}
          </span>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading && <Spinner />}
          {!loading && scenarios.length === 0 && (
            <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">
              {t('scenario.noSaved')}
            </div>
          )}
          {!loading && scenarios.map(s => (
            <div
              key={s.id}
              className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-surface)] transition-colors"
            >
              <button
                className="flex-1 text-left min-w-0"
                onClick={() => onLoad(s)}
              >
                <div className="text-xs font-medium text-[var(--aif-text-primary)] truncate">{s.name}</div>
                <div className="text-[10px] text-[var(--aif-text-muted)] mt-0.5">
                  {s.field_count ?? 0} {t('comparison.fields')} &middot; {s.created_at ? new Date(s.created_at).toLocaleDateString('de-DE') : ''}
                </div>
              </button>
              {s.scenario_total != null && (
                <span className="text-xs font-bold text-[var(--aif-text-primary)] flex-shrink-0">
                  {EUR.format(s.scenario_total)}
                </span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-500/10 transition text-[var(--aif-text-muted)] hover:text-red-400 flex-shrink-0"
                title={t('form.delete')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────

export default function ScenarioBuilder({ token, onClose }) {
  const { t } = useTranslation();

  // Data
  const [fields, setFields] = useState([]);           // current farm fields + DB data
  const [referenceCrops, setReferenceCrops] = useState([]);  // available crop reference data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scenario edits: { [field_id]: { crop_code, crop_name, yield_t_ha, price_per_t } }
  const [edits, setEdits] = useState({});

  // Comparison result from API
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);

  // UI state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null); // 'saved' | null
  const [expandedField, setExpandedField] = useState(null);

  // Debounce timer ref
  const debounceRef = useRef(null);

  // ── Fetch initial data ──────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    Promise.all([
      fetch('/api/v1/aif/finance/contribution-margin', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null),
      fetch('/api/v1/aif/finance/reference-data', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null),
    ])
      .then(([cmData, refData]) => {
        if (cmData?.per_field) {
          setFields(cmData.per_field);
        } else {
          setError(t('scenario.noFields'));
        }
        if (refData?.crops) {
          setReferenceCrops(refData.crops);
        } else if (Array.isArray(refData)) {
          setReferenceCrops(refData);
        }
      })
      .catch(() => setError(t('scenario.loadError')))
      .finally(() => setLoading(false));
  }, [token, t]);

  // ── Debounced comparison ────────────────────────────────────

  const triggerComparison = useCallback(() => {
    if (!token || Object.keys(edits).length === 0) {
      setComparison(null);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setComparing(true);
      try {
        const scenarioFields = fields.map(f => {
          const edit = edits[f.field_id];
          if (!edit) {
            return {
              field_id: f.field_id,
              crop_code: f.crop_code,
              yield_t_ha: f.per_ha?.yield_t_ha ?? f.yield_t_ha,
              price_per_t: f.per_ha?.price_per_t ?? f.price_per_t,
            };
          }
          return {
            field_id: f.field_id,
            crop_code: edit.crop_code || f.crop_code,
            yield_t_ha: edit.yield_t_ha ?? f.per_ha?.yield_t_ha ?? f.yield_t_ha,
            price_per_t: edit.price_per_t ?? f.per_ha?.price_per_t ?? f.price_per_t,
          };
        });

        const r = await apiClient.post('/api/v1/aif/finance/scenarios/compare', { fields: scenarioFields }, { entityType: 'scenario', queueOnOffline: false });
        if (r?.data) {
          setComparison(r.data);
        }
      } catch { /* ignore */ }
      setComparing(false);
    }, 300);
  }, [token, edits, fields]);

  useEffect(() => {
    triggerComparison();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [triggerComparison]);

  // ── Edit handlers ───────────────────────────────────────────

  const handleCropChange = (fieldId, cropCode) => {
    const ref = referenceCrops.find(c => c.crop_code === cropCode);
    const crop = ref || {};
    setEdits(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        crop_code: cropCode,
        crop_name: crop.crop_name || cropCode,
        yield_t_ha: crop.yield_t_ha ?? prev[fieldId]?.yield_t_ha,
        price_per_t: crop.price_per_t ?? prev[fieldId]?.price_per_t,
      },
    }));
  };

  const handleYieldChange = (fieldId, value) => {
    setEdits(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        yield_t_ha: value,
      },
    }));
  };

  const handlePriceChange = (fieldId, value) => {
    setEdits(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        price_per_t: value,
      },
    }));
  };

  const handleReset = () => {
    setEdits({});
    setComparison(null);
    setExpandedField(null);
  };

  // ── Save scenario ──────────────────────────────────────────

  const handleSave = async (name) => {
    setShowSaveDialog(false);
    if (!token || !name) return;

    const scenarioFields = fields.map(f => {
      const edit = edits[f.field_id];
      return {
        field_id: f.field_id,
        crop_code: edit?.crop_code || f.crop_code,
        yield_t_ha: edit?.yield_t_ha ?? f.per_ha?.yield_t_ha ?? f.yield_t_ha,
        price_per_t: edit?.price_per_t ?? f.per_ha?.price_per_t ?? f.price_per_t,
      };
    });

    try {
      await apiClient.post('/api/v1/aif/finance/scenarios', { name, fields: scenarioFields }, { entityType: 'scenario', queueOnOffline: true });
      setSaveFeedback('saved');
      setTimeout(() => setSaveFeedback(null), 2000);
    } catch { /* ignore */ }
  };

  // ── Load saved scenario ────────────────────────────────────

  const handleLoadScenario = (scenario) => {
    setShowSavedList(false);
    if (!scenario?.fields) return;

    const newEdits = {};
    for (const sf of scenario.fields) {
      const original = fields.find(f => f.field_id === sf.field_id);
      if (!original) continue;
      const origCrop = original.crop_code;
      const origYield = original.per_ha?.yield_t_ha ?? original.yield_t_ha;
      const origPrice = original.per_ha?.price_per_t ?? original.price_per_t;

      // Only set edits for fields that differ from current
      if (
        sf.crop_code !== origCrop ||
        sf.yield_t_ha !== origYield ||
        sf.price_per_t !== origPrice
      ) {
        const ref = referenceCrops.find(c => c.crop_code === sf.crop_code);
        newEdits[sf.field_id] = {
          crop_code: sf.crop_code,
          crop_name: ref?.crop_name || sf.crop_name || sf.crop_code,
          yield_t_ha: sf.yield_t_ha,
          price_per_t: sf.price_per_t,
        };
      }
    }
    setEdits(newEdits);
  };

  // ── Computed values ────────────────────────────────────────

  const currentTotal = fields.reduce((sum, f) => {
    const dbI = f.per_ha?.db_i ?? 0;
    const area = f.area_ha ?? 0;
    return sum + dbI * area;
  }, 0);

  const hasEdits = Object.keys(edits).length > 0;

  // ── Field row helpers ──────────────────────────────────────

  const getFieldDisplay = (f) => {
    const edit = edits[f.field_id];
    const isEdited = !!edit;
    const displayCrop = edit?.crop_code || f.crop_code;
    const displayCropName = edit?.crop_name || f.crop_name || f.crop_code;
    const displayYield = edit?.yield_t_ha ?? f.per_ha?.yield_t_ha ?? f.yield_t_ha;
    const displayPrice = edit?.price_per_t ?? f.per_ha?.price_per_t ?? f.price_per_t;
    const currentDbI = f.per_ha?.db_i ?? 0;
    const currentDbITotal = currentDbI * (f.area_ha ?? 0);

    // Per-field comparison from API result
    const fieldComp = comparison?.per_field?.find(cf => cf.field_id === f.field_id);
    const scenarioDbI = fieldComp?.scenario_db_i_per_ha;
    const scenarioDbITotal = fieldComp?.scenario_db_i_total;
    const vorfruchtEffect = fieldComp?.vorfrucht_effect_pct;

    return {
      isEdited,
      displayCrop,
      displayCropName,
      displayYield,
      displayPrice,
      currentDbI,
      currentDbITotal,
      scenarioDbI,
      scenarioDbITotal,
      vorfruchtEffect,
    };
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)] flex-1">
          {t('scenario.title')}
        </span>
        {comparing && (
          <div className="w-4 h-4 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent animate-spin flex-shrink-0" />
        )}
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-32">

        {/* Loading state */}
        {loading && <Spinner />}

        {/* Error state */}
        {error && !loading && (
          <div className="text-xs text-[var(--aif-text-muted)] text-center py-12">{error}</div>
        )}

        {/* Main content */}
        {!loading && !error && fields.length > 0 && (
          <div className="space-y-4 py-4">

            {/* ── Summary comparison card ── */}
            {comparison && (
              <div className="rounded-xl border-2 border-[var(--aif-color-primary)]/30 bg-[var(--aif-color-primary)]/5 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  {/* Current */}
                  <div className="text-center flex-1">
                    <div className="text-[10px] uppercase tracking-wide text-[var(--aif-text-muted)] font-medium">
                      {t('scenario.currentPlan')}
                    </div>
                    <div className="text-sm font-bold text-[var(--aif-text-primary)] mt-1">
                      {EUR.format(comparison.current_total ?? currentTotal)}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center pt-3">
                    <svg className="w-4 h-4 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>

                  {/* Scenario */}
                  <div className="text-center flex-1">
                    <div className="text-[10px] uppercase tracking-wide text-[var(--aif-text-muted)] font-medium">
                      {t('scenario.scenarioPlan')}
                    </div>
                    <div className="text-sm font-bold text-[var(--aif-text-primary)] mt-1">
                      {EUR.format(comparison.scenario_total ?? 0)}
                    </div>
                  </div>
                </div>

                {/* Delta */}
                <div className="text-center">
                  <DeltaBadge
                    current={comparison.current_total ?? currentTotal}
                    scenario={comparison.scenario_total ?? 0}
                  />
                </div>
              </div>
            )}

            {/* Current total when no comparison yet */}
            {!comparison && (
              <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-[var(--aif-text-muted)] font-medium">
                  {t('scenario.currentPlan')}
                </div>
                <div className="text-lg font-bold text-[var(--aif-text-primary)] mt-1">
                  {EUR.format(currentTotal)}
                </div>
                <div className="text-[10px] text-[var(--aif-text-muted)] mt-1">
                  {t('scenario.tapToChange')}
                </div>
              </div>
            )}

            {/* ── Field list ── */}
            <div className="space-y-2">
              {fields.map(f => {
                const d = getFieldDisplay(f);
                const isExpanded = expandedField === f.field_id;

                return (
                  <div
                    key={f.field_id}
                    className={`rounded-xl border ${
                      d.isEdited
                        ? 'border-[var(--aif-color-primary)]/40 bg-[var(--aif-color-primary)]/5'
                        : 'border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)]'
                    } overflow-hidden transition-colors`}
                  >
                    {/* Field header row */}
                    <button
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left active:bg-[var(--aif-bg-elevated)] transition-colors"
                      onClick={() => setExpandedField(isExpanded ? null : f.field_id)}
                    >
                      <span className="text-base flex-shrink-0">{cropEmoji(d.displayCrop)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-[var(--aif-text-primary)] truncate">
                          {f.field_name}
                          {f.area_ha && (
                            <span className="text-[var(--aif-text-muted)] font-normal ml-1">
                              {f.area_ha.toFixed(0)} ha
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[var(--aif-text-muted)] truncate mt-0.5">
                          {d.displayCropName}
                          {d.displayYield != null && (
                            <span> &middot; {NUM_DE.format(d.displayYield)} t/ha</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs font-bold text-[var(--aif-text-primary)]">
                          {EUR.format(d.currentDbI)}<span className="text-[9px] font-normal text-[var(--aif-text-muted)]">/ha</span>
                        </div>
                        <div className="text-[10px] text-[var(--aif-text-muted)]">
                          {EUR.format(d.currentDbITotal)}
                        </div>
                      </div>
                      <svg
                        className={`w-3.5 h-3.5 text-[var(--aif-text-muted)] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Expanded edit section */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-3 border-t border-[var(--aif-border-default)]">
                        {/* Crop selector */}
                        <div className="pt-3">
                          <label className="text-[10px] font-medium text-[var(--aif-text-muted)] uppercase tracking-wide block mb-1">
                            {t('scenario.changeCrop')}
                          </label>
                          <select
                            value={d.displayCrop || ''}
                            onChange={e => handleCropChange(f.field_id, e.target.value)}
                            className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-lg px-2.5 py-2 text-[var(--aif-text-primary)] outline-none focus:border-[var(--aif-color-primary)] transition-colors appearance-none cursor-pointer"
                          >
                            {f.crop_code && (
                              <option value={f.crop_code}>
                                {cropEmoji(f.crop_code)} {f.crop_name || f.crop_code} ({t('scenario.current')})
                              </option>
                            )}
                            {referenceCrops
                              .filter(c => c.crop_code !== f.crop_code)
                              .map(c => (
                                <option key={c.crop_code} value={c.crop_code}>
                                  {cropEmoji(c.crop_code)} {c.crop_name || c.crop_code}
                                </option>
                              ))
                            }
                          </select>
                        </div>

                        {/* Yield + Price overrides */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] font-medium text-[var(--aif-text-muted)] uppercase tracking-wide block mb-1">
                              {t('scenario.yield')}
                            </label>
                            <InlineNumberInput
                              value={d.displayYield}
                              onSave={v => handleYieldChange(f.field_id, v)}
                              unit="t/ha"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-[var(--aif-text-muted)] uppercase tracking-wide block mb-1">
                              {t('scenario.price')}
                            </label>
                            <InlineNumberInput
                              value={d.displayPrice}
                              onSave={v => handlePriceChange(f.field_id, v)}
                              unit="\u20AC/t"
                            />
                          </div>
                        </div>

                        {/* Per-field scenario result */}
                        {d.isEdited && comparison && d.scenarioDbI != null && (
                          <div className="rounded-lg bg-[var(--aif-bg-elevated)] p-2.5 space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-[var(--aif-text-muted)]">{t('scenario.scenarioDbI')}</span>
                              <span className="text-xs font-bold text-[var(--aif-text-primary)]">
                                {EUR.format(d.scenarioDbI)}/ha
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-[var(--aif-text-muted)]">{t('scenario.delta')}</span>
                              <DeltaBadge current={d.currentDbI} scenario={d.scenarioDbI} />
                            </div>
                            {d.vorfruchtEffect != null && d.vorfruchtEffect !== 0 && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">
                                  {t('scenario.vorfruchtEffect')}: {d.vorfruchtEffect > 0 ? '+' : ''}{d.vorfruchtEffect}%
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Inline delta when collapsed and edited */}
                    {!isExpanded && d.isEdited && comparison && d.scenarioDbI != null && (
                      <div className="px-3 pb-2 flex items-center justify-between">
                        <span className="text-[10px] text-[var(--aif-text-muted)]">
                          {t('scenario.scenarioLabel')}: {d.displayCropName}
                        </span>
                        <DeltaBadge current={d.currentDbI} scenario={d.scenarioDbI} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Per-field comparison table (when comparison active) ── */}
            {comparison?.per_field?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-[10px] font-bold text-[var(--aif-text-muted)] uppercase tracking-wide">
                  {t('scenario.fieldComparison')}
                </h3>
                <div className="rounded-xl border border-[var(--aif-border-default)] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px] min-w-[360px]">
                      <thead>
                        <tr className="bg-[var(--aif-bg-surface)] border-b border-[var(--aif-border-default)] text-left">
                          <th className="px-2.5 py-2 font-medium text-[var(--aif-text-muted)]">{t('comparison.field')}</th>
                          <th className="px-2.5 py-2 font-medium text-[var(--aif-text-muted)] text-right">{t('scenario.currentShort')}</th>
                          <th className="px-2.5 py-2 font-medium text-[var(--aif-text-muted)] text-right">{t('scenario.scenarioShort')}</th>
                          <th className="px-2.5 py-2 font-medium text-[var(--aif-text-muted)] text-right">{t('scenario.delta')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.per_field.map(cf => {
                          const origField = fields.find(f => f.field_id === cf.field_id);
                          const isChanged = edits[cf.field_id];
                          const delta = (cf.scenario_db_i_per_ha ?? 0) - (cf.current_db_i_per_ha ?? 0);
                          const positive = delta >= 0;

                          return (
                            <tr
                              key={cf.field_id}
                              className={`border-t border-[var(--aif-border-default)] ${isChanged ? 'bg-[var(--aif-color-primary)]/5' : ''}`}
                            >
                              <td className="px-2.5 py-1.5 text-[var(--aif-text-primary)] max-w-[100px] truncate">
                                {origField?.field_name || cf.field_name}
                              </td>
                              <td className="px-2.5 py-1.5 text-right text-[var(--aif-text-secondary)]">
                                {EUR.format(cf.current_db_i_per_ha ?? 0)}
                              </td>
                              <td className={`px-2.5 py-1.5 text-right font-medium ${isChanged ? 'text-[var(--aif-color-primary)]' : 'text-[var(--aif-text-secondary)]'}`}>
                                {EUR.format(cf.scenario_db_i_per_ha ?? 0)}
                              </td>
                              <td className={`px-2.5 py-1.5 text-right font-medium ${positive ? 'text-green-400' : 'text-red-400'}`}>
                                {positive ? '+' : ''}{EUR.format(delta)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom action bar ── */}
      {!loading && !error && fields.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 bg-[var(--aif-bg-page)] border-t border-[var(--aif-border-default)] px-4 py-3 flex flex-col gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {/* Save feedback */}
          {saveFeedback === 'saved' && (
            <div className="text-center text-xs text-green-400 font-medium py-1 animate-pulse">
              {t('scenario.savedSuccess')}
            </div>
          )}

          <div className="flex gap-2">
            {/* Reset */}
            <button
              onClick={handleReset}
              disabled={!hasEdits}
              className="flex-1 py-2.5 text-xs font-medium rounded-xl border border-[var(--aif-border-default)] text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-surface)] transition-colors disabled:opacity-30"
            >
              {t('scenario.reset')}
            </button>

            {/* Saved scenarios */}
            <button
              onClick={() => setShowSavedList(true)}
              className="flex-1 py-2.5 text-xs font-medium rounded-xl border border-[var(--aif-border-default)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] transition-colors"
            >
              {t('scenario.savedScenarios')}
            </button>

            {/* Save */}
            <button
              onClick={() => setShowSaveDialog(true)}
              disabled={!hasEdits}
              className="flex-1 py-2.5 text-xs font-medium rounded-xl bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-30"
            >
              {t('scenario.save')}
            </button>
          </div>
        </div>
      )}

      {/* ── Dialogs ── */}
      {showSaveDialog && (
        <SaveDialog
          onSave={handleSave}
          onCancel={() => setShowSaveDialog(false)}
          t={t}
        />
      )}
      {showSavedList && (
        <SavedScenariosPanel
          token={token}
          onLoad={handleLoadScenario}
          onClose={() => setShowSavedList(false)}
          t={t}
        />
      )}
    </div>
  );
}
